import { lightTheme } from '../styles/theme';
import { RestaurantReferralStatus } from '../types/restaurantReferral.types';

/*
 * Restaurant-to-restaurant referrals: the invite link, and carrying the
 * inviting location from that link to the sign up that follows it.
 *
 * Kept off the barrel's import graph (types and the theme only): the partner
 * app reads the link in `main.tsx`, before anything renders.
 */

/**
 * The restaurant OneLink the sales agents already hand out, with the inviting
 * location in `deep_link_value`. The apps read the same prefix, so this format
 * is shared with them and must not change on its own.
 *
 * `af_web_dp` is where the link sends a DESKTOP browser, which has no app to
 * open: this web app's own sign up, with the inviting location already in the
 * URL for `captureReferralFromUrl` - so the referral survives without relying
 * on what AppsFlyer forwards. It is built from the origin the link is made on,
 * so a link made on merchant-dev opens merchant-dev. AppsFlyer only honours it
 * for domains on the account's redirect allowlist; until a domain is there,
 * desktop keeps the template's own redirect. Phones ignore it.
 */
export const restaurantInviteLink = (
  locationId: number,
  webOrigin = typeof window === 'undefined' ? '' : window.location.origin
) => {
  const webSignUp = `${webOrigin}/login?referralLocationId=${locationId}`;
  return (
    'https://vgrubs-restaurant.onelink.me/prCZ?af_xp=custom&pid=invite' +
    `&af_dp=vgrubs%3A%2F%2Finvite&deep_link_value=location_${locationId}` +
    (webOrigin ? `&af_web_dp=${encodeURIComponent(webSignUp)}` : '')
  );
};

/** `location_<id>`, the value the link carries. */
const DEEP_LINK_LOCATION = /^location_(\d+)$/;

/**
 * Where the inviting location waits between the link and the sign up.
 *
 * localStorage rather than state: the link opens one page and the sign up can
 * take several, a reload or two and an OTP from another tab. Outside
 * `vg-admin-auth` so signing out does not lose it.
 */
const PENDING_REFERRAL_KEY = 'vg-referral-location';

const parseLocationId = (value: string | null) => {
  if (!value) return null;
  const fromDeepLink = DEEP_LINK_LOCATION.exec(value);
  const id = Number(fromDeepLink ? fromDeepLink[1] : value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * Remembers the inviting location when the page was opened from an invite.
 *
 * Accepts both the link's own `deep_link_value=location_<id>` (what the
 * OneLink passes on when it falls back to the web) and a plain
 * `referralLocationId=<id>`. A link without either leaves an earlier one in
 * place: the merchant may well open the app again before signing up.
 */
export const captureReferralFromUrl = () => {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const id =
      parseLocationId(params.get('deep_link_value')) ??
      parseLocationId(params.get('referralLocationId'));
    if (id) localStorage.setItem(PENDING_REFERRAL_KEY, String(id));
  } catch {
    // Storage can be off (private mode); the sign up simply goes unreferred.
  }
};

/**
 * The location to send as `referralLocationId`, if the merchant came from an
 * invite. The backend decides whether it counts - a returning user, the same
 * owner, an inactive referrer all fall away there - so this sends it whenever
 * there is one and never second-guesses it.
 */
export const getPendingReferralLocationId = (): number | undefined => {
  try {
    return (
      parseLocationId(localStorage.getItem(PENDING_REFERRAL_KEY)) ?? undefined
    );
  } catch {
    return undefined;
  }
};

/**
 * Once the wizard is complete the referral is written, so the link has done
 * its job - and a second location must not be credited to the same invite.
 */
export const clearPendingReferral = () => {
  try {
    localStorage.removeItem(PENDING_REFERRAL_KEY);
  } catch {
    // Nothing to clear.
  }
};

/**
 * The words for a status, the same in both apps - the merchant's, as the
 * mobile app has them. A live referral's progress is shown beside it, never
 * folded into the label.
 */
export const restaurantReferralStatusLabel = (
  status: RestaurantReferralStatus
) => {
  switch (status) {
    case 'signedUp':
      return 'Signed up';
    case 'live':
      return 'Live';
    case 'earned':
      return 'Earned';
    case 'expired':
      return 'Expired';
    default:
      return status;
  }
};

/**
 * A status's colour, the same in both apps and the same as the mobile app's:
 * orange while it waits to go live, blue while orders count, green once paid,
 * grey once it ran out. Both wear it as a solid pill
 * (`RestaurantReferralStatusPill`).
 */
export const restaurantReferralStatusColor = (
  status: RestaurantReferralStatus
) => {
  switch (status) {
    case 'signedUp':
      return lightTheme.orange;
    case 'live':
      return lightTheme.blue;
    case 'earned':
      return lightTheme.green;
    default:
      return lightTheme.lightGray;
  }
};

/**
 * A bonus as the offer says it: "$100", with cents only when there are any.
 * Its own formatter rather than `displayCentsInDollars` (always ".00"), which
 * lives in `helpers.tsx` and would pull that module's import cycle in here.
 */
const bonusFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export const formatReferralBonus = (cents: number) =>
  bonusFormatter.format((cents || 0) / 100);
